import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Edit3 } from "lucide-react";

/**
 * Generic CRUD table for sector modules. Props:
 *  - endpoint: e.g. "/chef/secteur/chantiers"
 *  - title, eyebrow, sub: page header
 *  - columns: array of { key, label, render?, className? }
 *  - fields: array of { key, label, type?, options?, required?, span? } for the dialog form
 *  - defaultRow: function returning empty form state
 *  - rowKey: column key to use as test id (default "id")
 *  - allowEdit: if true, shows edit button (uses PATCH endpoint)
 *  - emptyIcon, emptyText
 *  - testidPrefix: e.g. "chantier" -> add-btn becomes "chantier-add-btn"
 */
export default function SectorCRUDTable({
  endpoint,
  eyebrow,
  title,
  sub,
  columns,
  fields,
  defaultRow,
  allowEdit = false,
  emptyIcon: EmptyIcon,
  emptyText,
  testidPrefix,
  toRowExtras = () => ({}),
}) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultRow());

  const load = async () => {
    try { const { data } = await api.get(endpoint); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing && allowEdit) {
        await api.patch(`${endpoint}/${editing.id}`, form);
        toast.success("Mis à jour");
      } else {
        await api.post(endpoint, form);
        toast.success("Ajouté");
      }
      setOpen(false); setEditing(null); setForm(defaultRow()); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette entrée ?")) return;
    try { await api.delete(`${endpoint}/${id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openNew = () => { setEditing(null); setForm(defaultRow()); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...defaultRow(), ...row }); setOpen(true); };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        sub={sub}
        right={
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(defaultRow()); } }}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid={`${testidPrefix}-add-btn`} className="h-11 bg-[#002FA7] text-white"><Plus className="h-4 w-4 mr-2" /> Ajouter</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">{editing ? "Modifier" : "Nouvelle entrée"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-12 gap-3" data-testid={`${testidPrefix}-form`}>
                {fields.map((f) => (
                  <FieldRow key={f.key} field={f} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} testidPrefix={testidPrefix} />
                ))}
                <DialogFooter className="col-span-12 mt-2"><Button type="submit" data-testid={`${testidPrefix}-submit`} className="bg-[#002FA7] text-white">{editing ? "Mettre à jour" : "Ajouter"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-6 lg:p-10">
        <div className="bg-white border border-[#DEE2E6]" data-testid={`${testidPrefix}-list`}>
          {items.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#868E96]">
              {EmptyIcon && <EmptyIcon className="h-10 w-10 mx-auto mb-3 text-[#DEE2E6]" />}
              {emptyText || "Aucune entrée. Cliquez sur Ajouter."}
            </div>
          ) : (
            <>
              <div className="grid px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-bold border-b border-[#DEE2E6]" style={{ gridTemplateColumns: gridCols(columns) }}>
                {columns.map((c) => <div key={c.key} className={c.className}>{c.label}</div>)}
                <div className="text-right">Actions</div>
              </div>
              {items.map((row) => (
                <div key={row.id} className="grid px-5 py-3 items-center border-b border-[#F8F9FA] last:border-b-0 hover:bg-[#F8F9FA]" style={{ gridTemplateColumns: gridCols(columns) }} data-testid={`${testidPrefix}-row-${row.id}`} {...toRowExtras(row)}>
                  {columns.map((c) => <div key={c.key} className={`text-sm ${c.className || ""}`}>{c.render ? c.render(row[c.key], row) : (row[c.key] || "—")}</div>)}
                  <div className="flex justify-end gap-1">
                    {allowEdit && <Button size="sm" variant="outline" onClick={() => openEdit(row)} data-testid={`${testidPrefix}-edit-${row.id}`} className="h-7 px-2"><Edit3 className="h-3 w-3" /></Button>}
                    <Button size="sm" variant="outline" onClick={() => remove(row.id)} data-testid={`${testidPrefix}-delete-${row.id}`} className="h-7 px-2 border-[#E60000] text-[#E60000]"><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function gridCols(columns) {
  // Each column ~ "1fr". Actions: 100px
  return columns.map((c) => c.width || "1fr").join(" ") + " 100px";
}

function FieldRow({ field, value, onChange, testidPrefix }) {
  const span = field.span || 12;
  const cls = `col-span-${span}`;
  if (field.type === "select") {
    return (
      <div className={cls}>
        <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">{field.label}{field.required && <span className="text-[#E60000]"> *</span>}</Label>
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={`${testidPrefix}-field-${field.key}`} className="h-11 mt-1"><SelectValue placeholder={field.placeholder || ""} /></SelectTrigger>
          <SelectContent>
            {(field.options || []).map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className={cls}>
        <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">{field.label}{field.required && <span className="text-[#E60000]"> *</span>}</Label>
        <Textarea required={field.required} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={field.rows || 2} className="mt-1" data-testid={`${testidPrefix}-field-${field.key}`} />
      </div>
    );
  }
  return (
    <div className={cls}>
      <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">{field.label}{field.required && <span className="text-[#E60000]"> *</span>}</Label>
      <Input
        type={field.type || "text"}
        step={field.step}
        required={field.required}
        value={value ?? ""}
        onChange={(e) => onChange(field.type === "number" ? (e.target.value === "" ? "" : parseFloat(e.target.value)) : e.target.value)}
        className="h-11 mt-1"
        data-testid={`${testidPrefix}-field-${field.key}`}
        placeholder={field.placeholder}
      />
    </div>
  );
}
